import { useCallback, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { TrayRefProps } from '@cbhq/cds-web/overlays/tray/Tray';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { useRefreshWorkspaceMembers } from '@cbhq/identity-api-hooks/account-management/fetch';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { useStepUpSubmission } from '../../../hooks/useStepUpSubmission';
import type { EditMemberStatus } from '../components/EditMemberTray/EditMemberTrayHeader';
import { MemberRecentActivity } from '../components/EditMemberTray/MemberRecentActivity';
import { ProductAccessGroup } from '../components/InviteTray/ProductAccessGroup';
import { SaveFooter } from '../components/SaveFooter';
import { StepUpTwoFactorModal } from '../components/StepUpTwoFactorModal';
import { UnsavedChangesAlert } from '../components/UnsavedChangesAlert';
import { useEditSubmission } from '../useEditSubmission';
import { getSelections, useMemberEdit } from '../useMemberEdit';
import type { Member as TeamMember } from '../utils/constants';
import { isSingleEntityGatedRoleMutation } from '../utils/stepUp';
import { useRoles } from '../utils/useRoles';

import { EditMemberTray } from './EditMemberTray';

const messages = defineMessages({
  saveButton: {
    id: 'Team.singleLEEditTray.saveButton',
    defaultMessage: 'Save changes',
    description: 'Save button in the single-LE edit member tray',
  },
  saveSuccess: {
    id: 'Team.singleLEEditTray.saveSuccess',
    defaultMessage: 'Membership updated',
    description: 'Toast shown after successfully saving member role changes',
  },
  saveError: {
    id: 'Team.singleLEEditTray.saveError',
    defaultMessage: 'Error updating membership',
    description: 'Toast shown when saving member role changes fails',
  },
});

type SingleLEEditTrayProps = {
  member: TeamMember;
  status: EditMemberStatus;
  joinedLabel?: string;
  lastActiveLabel?: string;
  isEditable?: boolean;
  onClose: () => void;
};

export function SingleLEEditTray({
  member,
  status,
  joinedLabel,
  lastActiveLabel,
  isEditable = true,
  onClose,
}: SingleLEEditTrayProps) {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const trayRef = useRef<TrayRefProps>(null);
  const [alertVisible, setAlertVisible] = useState(false);
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? '';
  const refreshWorkspaceMembers = useRefreshWorkspaceMembers({ organizationId });
  const { roleMap, rolesByProductPerEntity } = useRoles();

  const legalEntityIds = useMemo(
    () =>
      (selectedOrganization?.legalEntities ?? [])
        .map((le) => le.legalEntityId)
        .filter((id): id is string => Boolean(id)),
    [selectedOrganization?.legalEntities],
  );

  const leRolesByProduct = rolesByProductPerEntity[legalEntityIds[0] ?? ''] ?? {};

  // Every defined role the member holds is seeded into the editable selection so
  // it starts checked — assignable roles stay toggleable, unassignable roles
  // render checked + disabled.
  const definedMemberRoles = useMemo(
    () => member.roles.filter((role) => roleMap[role] !== undefined),
    [member.roles, roleMap],
  );

  // Roles with no definition are preserved verbatim on save, since
  // updateOrganizationMemberRole() replaces the full per-entity set. Defined
  // roles live in `selections`, so excluding them here avoids a double-submit.
  const hiddenRoles = useMemo(
    () => member.roles.filter((role) => roleMap[role] === undefined),
    [member.roles, roleMap],
  );

  const { selections, rows, hasChanges } = useMemberEdit({
    memberRoles: definedMemberRoles,
    rolesByProduct: leRolesByProduct,
    roleMap,
  });
  const selectedRoles = useMemo(() => getSelections(selections), [selections]);

  const submissionRoles = useMemo(
    () => [...selectedRoles, ...hiddenRoles],
    [hiddenRoles, selectedRoles],
  );

  const isGatedMutation = useMemo(
    () => isSingleEntityGatedRoleMutation(member.roles, submissionRoles),
    [member.roles, submissionRoles],
  );

  const { submit, stepUpModalProps, resetStepUpRecovery, handleStepUpProofTokenError } =
    useStepUpSubmission({
      isGatedRoleMutation: isGatedMutation,
    });

  const handleSaveSuccess = useCallback(() => {
    resetStepUpRecovery();
    toast.show(formatMessage(messages.saveSuccess));
    void refreshWorkspaceMembers();
    onClose();
  }, [formatMessage, onClose, refreshWorkspaceMembers, resetStepUpRecovery, toast]);

  const handleSaveFailure = useCallback(
    (error: unknown) => {
      if (handleStepUpProofTokenError(error)) {
        return;
      }
      toast.show(formatMessage(messages.saveError));
    },
    [formatMessage, handleStepUpProofTokenError, toast],
  );

  const { isSaving, saveEdit } = useEditSubmission({
    organizationId,
    memberUuid: member.userUuid,
    selectedRoles: submissionRoles,
    legalEntityIds,
    isFormValid: hasChanges,
    onSuccess: handleSaveSuccess,
    onFailure: handleSaveFailure,
  });

  const handleSave = useCallback(() => {
    submit(saveEdit);
  }, [saveEdit, submit]);

  const name = [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email;

  const handleBeforeClose = useCallback(() => {
    if (hasChanges) {
      setAlertVisible(true);
      return;
    }

    trayRef.current?.close();
  }, [hasChanges]);

  const handleKeepEditing = useCallback(() => {
    setAlertVisible(false);
  }, []);

  const handleDiscard = useCallback(() => {
    setAlertVisible(false);
    trayRef.current?.close();
  }, []);

  const footer = useMemo(() => {
    if (!isEditable) {
      return null;
    }

    return (
      <SaveFooter visible={hasChanges || isSaving} onSave={handleSave} loading={isSaving}>
        {formatMessage(messages.saveButton)}
      </SaveFooter>
    );
  }, [formatMessage, handleSave, hasChanges, isEditable, isSaving]);

  const recentActivity = useMemo(
    () => <MemberRecentActivity organizationId={organizationId} memberUuid={member.userUuid} />,
    [member.userUuid, organizationId],
  );

  return (
    <>
      <EditMemberTray
        name={name}
        email={member.email}
        status={status}
        joinedLabel={joinedLabel}
        lastActiveLabel={lastActiveLabel}
        avatarSrc={member.avatarUrl}
        trayRef={trayRef}
        footer={footer}
        recentActivity={recentActivity}
        beforeClose={handleBeforeClose}
        onClose={onClose}
      >
        <ProductAccessGroup rows={rows} isEditable={isEditable} />
      </EditMemberTray>
      <StepUpTwoFactorModal {...stepUpModalProps} />
      <UnsavedChangesAlert
        variant="edit"
        visible={alertVisible}
        memberName={name}
        onKeepEditing={handleKeepEditing}
        onDiscard={handleDiscard}
      />
    </>
  );
}
